import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { useAllObjectClassListQuery } from '@/hook';
import { Close, VFlex, VInput, VStack } from '@/package';
import type { ModelTrainingForm, Task } from '@/type';

interface SplitCardProps {
  task: Task;
  deletable: boolean;
  disabled: boolean;
  watch: UseFormWatch<ModelTrainingForm>;
  setValue: UseFormSetValue<ModelTrainingForm>;
  updateTaskSplitRatio: (
    taskId: Task['id'],
    training: number,
    validation: number,
  ) => void;
  getTaskSplitRatio: (taskId: Task['id']) => {
    training: number;
    validation: number;
  };
}

const SplitCard = ({
  task,
  deletable = true,
  disabled = false,
  watch,
  setValue,
  updateTaskSplitRatio,
  getTaskSplitRatio,
}: SplitCardProps) => {
  const { data: labelItemList } = useAllObjectClassListQuery();
  const enabledLabelItemList = labelItemList?.filter(item => item.enable);

  const splitMode = watch('dataSettingMode');
  const isAutoMode = splitMode === 'auto';

  const objectEntryList =
    task.statExtend?.map(stat => {
      const extendClass = enabledLabelItemList?.find(
        item => +item.id === +stat.extendClassID,
      );

      return {
        label: extendClass?.alias || extendClass?.name,
        count: stat.humanCountValue + stat.aiCountValue,
      };
    }) ?? [];

  const totalObjectCount = objectEntryList.reduce(
    (acc, item) => acc + item.count,
    0,
  );

  const currentRatios = getTaskSplitRatio(task.id);

  const handleTrainingChange = (value: string) => {
    const trainingValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    const validationValue = 100 - trainingValue;

    updateTaskSplitRatio(task.id, trainingValue, validationValue);
  };

  const handleValidationChange = (value: string) => {
    const validationValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    const trainingValue = 100 - validationValue;

    updateTaskSplitRatio(task.id, trainingValue, validationValue);
  };

  const handleRemoveTask = () => {
    if (disabled) return;

    const currentSelectedTaskList = watch('selectedTaskList');
    setValue(
      'selectedTaskList',
      currentSelectedTaskList.filter(t => t.id !== task.id),
    );
  };

  return (
    <VStack className="w-full divide-y divide-gray-300 rounded-md border border-gray-300 p-2">
      <VFlex className="pb-1" justify="between" items="center">
        <VStack>
          <VFlex className="gap-[5px]">
            <div className="text-body-lg text-gray-400">{task.id}</div>
            <div className="text-body-lg">{task.taskName}</div>
          </VFlex>
          {task.project?.name && (
            <div className="text-body-sm text-gray-400">
              {task.project.name}
            </div>
          )}
        </VStack>
        {deletable && (
          <Close
            className="size-4 text-gray-400"
            role={disabled ? 'presentation' : 'button'}
            onClick={handleRemoveTask}
          />
        )}
      </VFlex>

      <VStack className="w-full divide-y divide-gray-300" gap={0.25}>
        <VStack className="w-full divide-y divide-gray-200" gap={0.25}>
          <VFlex
            justify="between"
            items="center"
            className="px-2 text-body-lg font-semibold text-gray-400"
          >
            <div>Total data</div>
            <div>{Intl.NumberFormat('en-us').format(totalObjectCount)}</div>
          </VFlex>

          <VFlex
            className="px-2 pb-1 text-body-md text-gray-400"
            wrap="wrap"
            justify="between"
            gap={0.25}
          >
            {objectEntryList.map((item, index) => {
              return (
                <VFlex
                  key={index}
                  className="w-[calc(50%-4px)]"
                  justify="between"
                  items="center"
                >
                  <div className="capitalize">{item.label}</div>
                  <div>
                    <strong>
                      {Intl.NumberFormat('en-us').format(item.count)}
                    </strong>
                  </div>
                </VFlex>
              );
            })}
          </VFlex>
        </VStack>

        {isAutoMode ? (
          <div className="px-2 text-body-md text-gray-500">
            Automatically sets training and validation ratios.
          </div>
        ) : (
          <VFlex gap={0.5}>
            <VInput
              className="flex-1"
              size="sm"
              label="Training"
              orientation="horizontal"
              type="number"
              value={currentRatios.training.toString()}
              onChange={e => handleTrainingChange(e.target.value)}
              min={0}
              max={100}
              disabled={disabled}
            />
            <VInput
              className="flex-1"
              size="sm"
              label="Validation"
              orientation="horizontal"
              type="number"
              value={currentRatios.validation.toString()}
              onChange={e => handleValidationChange(e.target.value)}
              min={0}
              max={100}
              disabled={disabled}
            />
          </VFlex>
        )}
      </VStack>
    </VStack>
  );
};

export default SplitCard;
